import type { ItemRewards } from '../../shared/quests.ts';
import { el, restartAnimation } from '../common/dom.ts';
import { icon } from '../common/icons.ts';
import { itemCatalog, itemVisual } from '../common/item-catalog.ts';

/** Temps pendant lequel le contenu reste à l'écran, une fois le dernier objet apparu. */
const SHOWN_MS = 2200;
/** Écart entre l'apparition de deux objets. */
const STAGGER_MS = 140;

/**
 * Ce que le joueur vient de trouver en fouillant un objet : ses objets (et ses pièces) surgissent au milieu de l'écran devant un
 * halo de lumière, puis s'effacent d'eux-mêmes. Rien à fermer : le joueur peut continuer à jouer pendant ce temps.
 */
export class LootReveal {
  private readonly root = el('div', { className: 'loot-reveal', hidden: true });
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(parent: HTMLElement) {
    this.root.setAttribute('aria-hidden', 'true');
    parent.append(this.root);
  }

  /** Objets et pièces trouvés, montrés au milieu de l'écran (rien s'il n'y a rien). */
  show({ items, coins }: ItemRewards): void {
    if (!items.length && !coins) return;
    const entries = [
      ...items.map(({ item, quantity }) => this.entry(itemVisual(itemCatalog.get(item)), `${itemCatalog.name(item)}${quantity > 1 ? ` ×${quantity}` : ''}`)),
      ...(coins ? [this.entry(icon('coins', { color: '#ffd640' }), `${coins} pièce${coins > 1 ? 's' : ''}`)] : []),
    ];
    entries.forEach((entry, index) => entry.style.setProperty('--delay', `${index * STAGGER_MS}ms`));
    const total = SHOWN_MS + entries.length * STAGGER_MS;
    this.root.style.setProperty('--total', `${total}ms`);
    this.root.replaceChildren(el('div', { className: 'loot-reveal-glow' }), el('div', { className: 'loot-reveal-items' }, ...entries));
    this.root.hidden = false;
    restartAnimation(this.root, 'playing');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.root.hidden = true; }, total);
  }

  private entry(visual: HTMLElement, label: string): HTMLElement {
    return el('div', { className: 'loot-reveal-item' }, el('div', { className: 'loot-reveal-icon' }, visual), el('span', { textContent: label }));
  }
}
